import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { MedicationDetail } from "@/modules/patients/detail/medication/components/ch-medication-search"
import { handleMedicationSearchItem } from "@/modules/patients/detail/medication/services/useChMedicationSearchItemQuery"
import {
  RequestedMedication,
  RequestedMedicationItem,
} from "@/modules/patients/detail/prescription/ch/components/requested-medication-item"
import { RequestedMedicationsFragment$key } from "@/modules/patients/services/__generated__/RequestedMedicationsFragment.graphql"
import { RequestedMedicationsFragment } from "@/modules/patients/services/RequestedMedicationsFragment"
import { Language } from "@/types/__generated__/union-types"

interface ChRequestedMedicationsProps {
  requestedMedicationsRef: RequestedMedicationsFragment$key | null
  patientLanguage: Language
  onSelectRequestedMedication: (medication: MedicationDetail) => void
}

export const ChRequestedMedications = ({
  requestedMedicationsRef,
  patientLanguage,
  onSelectRequestedMedication,
}: ChRequestedMedicationsProps) => {
  const { t } = useTranslation()
  const requestedMedicationsData = useFragment<RequestedMedicationsFragment$key>(
    RequestedMedicationsFragment,
    requestedMedicationsRef,
  )
  const requestedMedications = requestedMedicationsData?.requestedMedications

  const [isMedicationDetailLoading, setIsMedicationDetailLoading] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    return () => abortControllerRef.current?.abort()
  }, [])

  if (!requestedMedications?.length) {
    return null
  }

  const handleSelectRequestedMedication = async (requestedMedication: RequestedMedication) => {
    const { name, mainSubstance, pharmaCode } = requestedMedication

    // A requested medication with no pharmacode is an active-substance request, not a specific product.
    if (!pharmaCode) {
      onSelectRequestedMedication({ name, mainSubstance, medicationType: "SUBSTANCE" })
      return
    }

    abortControllerRef.current = new AbortController()

    setIsMedicationDetailLoading(true)

    try {
      const medicationDetail = await handleMedicationSearchItem(
        abortControllerRef.current.signal,
        "PREPARATION",
        patientLanguage,
        undefined,
        pharmaCode,
      )

      if (medicationDetail) {
        onSelectRequestedMedication({
          ...medicationDetail,
          name,
          itemId: pharmaCode,
          pharmacode: pharmaCode,
          medicationType: "PREPARATION",
        })
      }
    } finally {
      setIsMedicationDetailLoading(false)
    }
  }

  return (
    <div className="mb-4 flex flex-col gap-2" data-testid="requested-medications">
      <p className="text-on-surface-subdued text-sm">{t("episodeOfCare.progressNote.requestedMedications.title")}</p>
      <div className="flex flex-col gap-0.5">
        {requestedMedications.map((requestedMedication) => (
          <RequestedMedicationItem
            key={`${requestedMedication.name}-${requestedMedication.mainSubstance ?? ""}-${requestedMedication.packageSize ?? ""}-${requestedMedication.pharmaCode ?? ""}`}
            requestedMedication={requestedMedication}
            isMedicationDetailLoading={isMedicationDetailLoading}
            onSelectRequestedMedication={handleSelectRequestedMedication}
          />
        ))}
      </div>
    </div>
  )
}
